"use client";
import { Box } from "@dxp-feed/ui/Box";
import {
  useAppDispatch,
  useAppSelector,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "@/utils/hooks/reactUseCustomHooks";
import { RecipeGrid } from "./_components/RecipeGrid";
import { Text } from "@dxp-feed/ui/Text";
import { Button } from "@dxp-feed/ui/Button";
import SideBarTemplate from "../algolia/SideBarTemplate";
import usePageHandler from "./common";
import { DEFAULT_RECIPE_FILTER, SHORTING_RECIPES } from "@/utils/constant";
import { useSortBy } from "@dxp-feed/algolia";
import useScrollBlocker from "./common/commonRecipeFunctions/useScrollBlocker";
import { setMessages } from "@/utils/redux/recipes/recipeSlice";
import { setMessages as setMealPlanMessages } from "@/utils/redux/mealPlan/mealPlanSlice";
import { setAemData } from "@/utils/redux/recipes/aemContentSlice";
import { setClearFilters } from "@/utils/redux/recipes/algoliaSlice";
import { BreadcrumbData, RecipeType } from "@/utils/types/recipes";
import FeaturedRecipe from "../home/_components/Content/RecipeCarousal";
import SectionTitle from "../home/_components/Content/SectionTitle";
import { aemSortOrder } from "./common/commonRecipeFunctions";
import useRecipes from "./common/commonRecipeFunctions/getRecipes";
import ToastMessage from "../common/ToastMessage";
import CategorieList from "./_components/CategorieList";
interface RecipeListingProps {
  text?: string;
  isSearchPage?: boolean;
  searchQuery?: string;
  aemServerData?: any;
  aemRecipeData?: any;
  preferredResults?: RecipeType[];
}

const Recipes: React.FC<RecipeListingProps> = ({
  text,
  isSearchPage = false,
  searchQuery = "",
  aemServerData,
  aemRecipeData,
  preferredResults,
}) => {
  const [isUlVisible, setIsUlVisible] = useState(false);
  const [isSortVisible, setIsSortVisible] = useState(false);
  const { pageFilters } = useAppSelector((state) => state.algoliaDataSlice);
  const dispatch = useAppDispatch();
  const { aemFilterData }: any = useAppSelector(
    (state) => state.getAemDataSlice
  );
  const { bodyRef, disableBackgroundScroll, enableBackgroundScroll } =
    useScrollBlocker();
  const toggleFilter = useCallback(() => setIsUlVisible((prev) => !prev), []);
  const toggleSort = useCallback(() => {
    setIsSortVisible((prev) => !prev);
    disableBackgroundScroll();
  }, []);

  useRecipes();
  useEffect(() => {
    (window as any).SalesforceInteractions?.init({
      consents: [
        {
          provider: "OneTrust",
          purpose: "Tracking",
          status: (window as any).SalesforceInteractions.ConsentStatus.OptIn,
        },
      ],
    });

    const getAllData = async () => {
      dispatch(setMessages(aemServerData?.successAndErrorMessages));
      dispatch(setMealPlanMessages(aemServerData?.successAndErrorMessages));
      dispatch(setAemData(aemServerData?.aemData));
    };
    void getAllData();
    dispatch(setClearFilters());
  }, []);

  const closeSortHandler = useCallback(() => {
    setIsSortVisible(false);
    enableBackgroundScroll();
  }, []);
  const sortUlHandler = useCallback(() => setIsUlVisible(false), []);

  const breadcrumb: BreadcrumbData[] = useMemo(
    () => [
      { text: "Home", href: "/" },
      { text: "Recipes", href: "" },
    ],
    []
  );

  const { options, currentRefinement } = useSortBy({
    items: SHORTING_RECIPES,
  });
  usePageHandler();
  /** Setting Sort Label */
  const heading: string = useMemo(() => {
    return options.find((option) => option.value === currentRefinement)
      ?.label as string;
  }, [options, currentRefinement]);
  return (
    <>
      {DEFAULT_RECIPE_FILTER && (
        <SideBarTemplate
          breadcrumbList={breadcrumb}
          isUlVisible={isUlVisible}
          closeUl={sortUlHandler}
          filterData={aemSortOrder(aemFilterData)}
          isSortVisible={isSortVisible}
          closeSort={closeSortHandler}
          isSearchPage={isSearchPage}
          type="recipes"
          clearFilterAttributes={[]}
          searchQuery={searchQuery}
          className={!searchQuery && isSearchPage ? `[&_.sideBar]:hidden` : ""}
          filters={DEFAULT_RECIPE_FILTER}
          pagePath="recipes"
          preferredResults={preferredResults}
        >
          {!isSearchPage && (
            <Text as="h2" className="text-[28px] font-bold pb-6 font-sans">
              Recipes{" "}
            </Text>
          )}
          {!isSearchPage && (
            <>
              <CategorieList
                aemCategorieData={aemRecipeData?.recipeCategories}
              />
              <Box className="[&>div]:px-0 mt-[-32px] lg:mt-[-40px]">
                <SectionTitle
                  as="h3"
                  title={
                    aemRecipeData?.aemFeaturedRecipe?.recipeHeading?.heading ||
                    "Featured Recipes"
                  }
                  titleClassname="!text-[28px] lg:!text-[24px]"
                />
              </Box>
              <Box className="pb-6 [&_.recipeSecton>:first-child]:pr-0 [&_.recipeSecton>:last-child]:mr-[-24px] 2md:[&_.recipeSecton>:last-child]:mr-[-48px] lg:[&_.recipeSecton>:last-child]:mr-0">
                <FeaturedRecipe
                  homepageBanner={{
                    banner:
                      aemRecipeData?.aemFeaturedRecipe
                        ?.homePageRecipeBannerData,
                    recipeheading:
                      aemRecipeData?.aemFeaturedRecipe?.recipeHeading,
                  }}
                  homePageRecipeData={aemRecipeData?.aemFeaturedRecipe?.featuredRecipe?.map(
                    (recipe: any) => ({
                      ...recipe,
                      objectID: recipe.id,
                      featuredImage: recipe.image,
                    })
                  )}
                />
              </Box>
            </>
          )}
          <ToastMessage />
          <Box as="div">
            {!!searchQuery === isSearchPage && (
              <Box
                className="flex justify-between items-center flex-col"
                ref={bodyRef}
              >
                {text && (
                  <Text as="h3" className="title-tertiary pb-6 w-full">
                    {aemRecipeData?.recipe?.title?.heading}
                  </Text>
                )}
                <Box
                  as="div"
                  className="bg-grey100 filter-mobile lg:bg-transparent lg:hidden flex flex-col 2xs:flex-row w-full py-4 gap-2 mb-6 order-1 md:order-2 md:justify-start relative before:content-[''] before:absolute after:content-[''] after:absolute before:block after:block before:h-full after:h-full before:bg-grey100 after:bg-grey100 before:w-6 after:w-6 before:left-[-24px] after:right-[-24px]  before:top-0  after:top-0 lg:before:content-none lg:after:content-none 
            
            2md:before:w-12 2md:after:w-12 2md:before:left-[-48px] 2md:after:right-[-48px]
            "
                >
                  <Button
                    variant="outlneWhite"
                    onClick={toggleFilter}
                    aria-label={`${aemFilterData?.["filters"] ?? "Filters"}{" "}
                ${
                  pageFilters?.["recipes"]
                    ? `(${pageFilters?.["recipes"]?.filters ?? ""})`
                    : ""
                }`}
                    className="w-full md:w-auto border bg-brandWhite border-grey350 font-light text-[14px] 2xs:text-[12px] xs:text-[14px]  gap-2 h-10"
                  >
                    <span className="demo-icon icon-filter w-[18px] h-[18px] text-[11px] flex items-center justify-center text-[#1C1B1F]"></span>{" "}
                    {aemFilterData?.["filters"] ?? "Filters"}{" "}
                    {pageFilters?.["recipes"]
                      ? `(${pageFilters?.["recipes"]?.filters ?? ""})`
                      : ""}
                  </Button>
                  <Button
                    variant="outlneWhite"
                    onClick={toggleSort}
                    aria-label={heading}
                    className="w-full md:w-auto border bg-brandWhite border-grey350 font-light text-[14px] 2xs:text-[12px] xs:text-[14px] gap-2 h-10"
                  >
                    <span className="demo-icon icon-swap w-[18px] h-[18px] text-[18px] flex items-center justify-center text-[#1C1B1F]"></span>
                    {heading}
                  </Button>
                </Box>
              </Box>
            )}
            <Box as="div" className="recipeMain">
              <RecipeGrid
                list_name="Recipes"
                searchQuery={searchQuery}
                isSearchPage={isSearchPage}
              />
            </Box>
          </Box>
        </SideBarTemplate>
      )}
    </>
  );
};

export default Recipes;
